import { useState, useEffect, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import ElegantHeader from '@/components/feature/ElegantHeader';
import ElegantFooter from '@/components/feature/ElegantFooter';
import ElegantSectionTitle from '@/components/feature/ElegantSectionTitle';

/* ─── Types ─── */
export interface ServiceItem {
  icon: string;
  title: string;
  desc: string;
}

export interface StatItem {
  number: string;
  label: string;
}

export interface StepItem {
  title: string;
  desc: string;
}

export interface ServicePageConfig {
  pageLabel: string;
  tag: string;
  title: ReactNode;
  subtitle: string;
  icon: string;
  services: ServiceItem[];
  stats?: StatItem[];
  steps?: StepItem[];
  benefits: string[];
  extraSection?: ReactNode;
  heroImage?: string;
}

interface Props {
  config: ServicePageConfig;
}

export default function ServicePageLayout({ config }: Props) {
  const { t } = useTranslation();

  const { ref: servicesRef, visible: servicesVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: statsRef, visible: statsVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: stepsRef, visible: stepsVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: benefitsRef, visible: benefitsVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: ctaRef, visible: ctaVisible } = useScrollReveal({ threshold: 0.15 });

  return (
    <div className="min-h-screen" style={{ background: '#FAF9F6' }}>
      <ElegantHeader />

      {/* ── Hero ── */}
      <div
        className="relative w-full flex items-end overflow-hidden"
        style={{
          minHeight: '400px',
          paddingBottom: '64px',
          paddingTop: '120px',
          background: '#0A192F',
        }}
      >
        {/* Background Image */}
        {config.heroImage && (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${config.heroImage})`,
                transform: 'scale(1.05)',
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(180deg, rgba(10,25,47,0.7) 0%, rgba(10,25,47,0.85) 60%, rgba(10,25,47,0.95) 100%)',
              }}
            />
          </>
        )}

        {/* Ambient glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute"
            style={{
              width: '500px',
              height: '500px',
              background: 'radial-gradient(circle, rgba(201,162,39,0.08) 0%, transparent 65%)',
              top: '-20%',
              right: '10%',
              filter: 'blur(60px)',
            }}
          />
        </div>

        {/* Gold accent lines */}
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(201,162,39,0.3), transparent)' }}
        />

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 w-full">
          {/* Breadcrumb */}
          <nav className="mb-5 text-sm flex items-center gap-2 flex-wrap">
            <Link
              to="/"
              className="no-underline transition-colors duration-300"
              style={{ color: 'rgba(255,255,255,0.4)' }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.7)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.4)';
              }}
            >
              {t('nav.topPage')}
            </Link>
            <span style={{ color: 'rgba(255,255,255,0.2)' }}>/</span>
            <Link
              to="/business"
              className="no-underline transition-colors duration-300"
              style={{ color: 'rgba(255,255,255,0.4)' }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.7)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.4)';
              }}
            >
              {t('nav.business')}
            </Link>
            <span style={{ color: 'rgba(255,255,255,0.2)' }}>/</span>
            <span style={{ color: 'rgba(255,255,255,0.65)' }}>{config.pageLabel}</span>
          </nav>

          {/* Tag */}
          <div className="flex items-center gap-3 mb-5">
            <div
              className="w-8 h-px"
              style={{ background: 'linear-gradient(90deg, #C9A227, transparent)' }}
            />
            <p
              className="text-xs tracking-widest-plus uppercase"
              style={{
                color: '#C9A227',
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
              }}
            >
              {config.tag}
            </p>
          </div>

          {/* Title */}
          <div className="flex items-start gap-4">
            <div
              className="flex-shrink-0 w-12 h-12 flex items-center justify-center mt-1"
              style={{
                border: '1px solid rgba(201, 162, 39, 0.3)',
                background: 'rgba(201, 162, 39, 0.1)',
              }}
            >
              <i className={`${config.icon} text-xl`} style={{ color: '#C9A227' }} />
            </div>
            <h1
              className="text-3xl md:text-4xl lg:text-5xl leading-tight"
              style={{
                fontFamily: "'Noto Serif JP', 'Playfair Display', serif",
                fontWeight: 500,
                color: '#FFFFFF',
                letterSpacing: '0.04em',
              }}
            >
              {config.title}
            </h1>
          </div>

          <p
            className="mt-5 max-w-2xl leading-relaxed"
            style={{
              color: 'rgba(255,255,255,0.55)',
              fontFamily: "'Noto Serif JP', serif",
              fontSize: '15px',
            }}
          >
            {config.subtitle}
          </p>
        </div>

        {/* Bottom gold line */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(201,162,39,0.2), transparent)' }}
        />
      </div>

      {/* ── Services Grid ── */}
      <section
        ref={servicesRef as React.RefObject<HTMLElement>}
        className="py-20 md:py-28 px-6 md:px-10 max-w-[1400px] mx-auto"
      >
        <div className="mb-16">
          <ElegantSectionTitle
            tag="Services"
            title={t('seo.servicePageTitle')}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {config.services.map((svc, i) => (
            <div
              key={i}
              className="group p-7 border transition-all duration-300"
              style={{
                background: '#FFFFFF',
                borderColor: 'rgba(212, 207, 196, 0.4)',
                opacity: servicesVisible ? 1 : 0,
                transform: servicesVisible ? 'translateY(0)' : 'translateY(28px)',
                transition: `opacity 0.65s ease ${0.1 + i * 0.07}s, transform 0.65s ease ${0.1 + i * 0.07}s, border-color 0.3s`,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(201, 162, 39, 0.3)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(212, 207, 196, 0.4)';
              }}
            >
              <div
                className="w-10 h-10 flex items-center justify-center mb-5"
                style={{
                  border: '1px solid rgba(201, 162, 39, 0.2)',
                  background: 'rgba(201, 162, 39, 0.05)',
                }}
              >
                <i className={`${svc.icon} text-lg`} style={{ color: '#C9A227' }} />
              </div>
              <h3
                className="text-base mb-2"
                style={{
                  fontFamily: "'Noto Serif JP', serif",
                  fontWeight: 500,
                  color: '#1A1A1A',
                }}
              >
                {svc.title}
              </h3>
              <p
                className="text-sm leading-relaxed"
                style={{
                  color: '#7A7569',
                  fontFamily: "'Noto Serif JP', serif",
                }}
              >
                {svc.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Extra section (optional) ── */}
      {config.extraSection && (
        <section className="pb-4 px-6 md:px-10 max-w-[1400px] mx-auto">
          {config.extraSection}
        </section>
      )}

      {/* ── Stats ── */}
      {config.stats && config.stats.length > 0 && (
        <section
          ref={statsRef as React.RefObject<HTMLElement>}
          className="py-16 md:py-20 px-6 md:px-10"
          style={{ background: '#0A192F' }}
        >
          <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6 text-center">
            {config.stats.map((s, i) => (
              <div
                key={i}
                className="space-y-2"
                style={{
                  opacity: statsVisible ? 1 : 0,
                  transform: statsVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.65s ease ${i * 0.1}s, transform 0.65s ease ${i * 0.1}s`,
                }}
              >
                <div
                  className="text-3xl md:text-4xl"
                  style={{
                    fontFamily: "'Playfair Display', 'Noto Serif JP', serif",
                    fontWeight: 500,
                    color: '#C9A227',
                    letterSpacing: '0.02em',
                  }}
                >
                  {s.number}
                </div>
                <div
                  className="text-xs tracking-widest-plus uppercase"
                  style={{
                    color: 'rgba(255,255,255,0.5)',
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Process Steps ── */}
      {config.steps && config.steps.length > 0 && (
        <section
          ref={stepsRef as React.RefObject<HTMLElement>}
          className="py-20 md:py-28 px-6 md:px-10 max-w-[1400px] mx-auto"
        >
          <div className="mb-16">
            <ElegantSectionTitle
              tag="Process"
              title={t('seo.servicePageProcess')}
            />
          </div>

          <div className="relative max-w-3xl mx-auto">
            {/* Vertical line */}
            <div
              className="absolute left-[23px] top-0 bottom-0 w-px hidden md:block"
              style={{ background: 'linear-gradient(180deg, rgba(201,162,39,0.2), rgba(201,162,39,0.1))' }}
            />
            <div className="space-y-6">
              {config.steps.map((step, i) => (
                <div
                  key={i}
                  className="flex gap-5 items-start"
                  style={{
                    opacity: stepsVisible ? 1 : 0,
                    transform: stepsVisible ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `opacity 0.65s ease ${i * 0.1}s, transform 0.65s ease ${i * 0.1}s`,
                  }}
                >
                  <div
                    className="flex-shrink-0 w-12 h-12 flex items-center justify-center text-sm font-medium z-10"
                    style={{
                      background: '#C9A227',
                      color: '#0A192F',
                      fontFamily: "'Playfair Display', serif",
                    }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div
                    className="flex-1 p-6 border"
                    style={{
                      background: '#FFFFFF',
                      borderColor: 'rgba(212, 207, 196, 0.4)',
                    }}
                  >
                    <h3
                      className="text-base mb-2"
                      style={{
                        fontFamily: "'Noto Serif JP', serif",
                        fontWeight: 500,
                        color: '#1A1A1A',
                      }}
                    >
                      {step.title}
                    </h3>
                    <p
                      className="text-sm leading-relaxed"
                      style={{
                        color: '#7A7569',
                        fontFamily: "'Noto Serif JP', serif",
                      }}
                    >
                      {step.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Benefits ── */}
      <section
        ref={benefitsRef as React.RefObject<HTMLElement>}
        className="py-20 md:py-28 px-6 md:px-10"
        style={{ background: '#FFFFFF' }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-16">
            <ElegantSectionTitle
              tag="Benefits"
              title={t('seo.servicePageBenefits')}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {config.benefits.map((b, i) => (
              <div
                key={i}
                className="flex items-start gap-4 p-5 border"
                style={{
                  background: '#FAF9F6',
                  borderColor: 'rgba(212, 207, 196, 0.4)',
                  opacity: benefitsVisible ? 1 : 0,
                  transform: benefitsVisible ? 'translateY(0)' : 'translateY(16px)',
                  transition: `opacity 0.6s ease ${i * 0.07}s, transform 0.6s ease ${i * 0.07}s`,
                }}
              >
                <div
                  className="flex-shrink-0 w-7 h-7 flex items-center justify-center"
                  style={{
                    border: '1px solid rgba(201, 162, 39, 0.25)',
                  }}
                >
                  <i className="ri-check-line text-xs" style={{ color: '#C9A227' }} />
                </div>
                <p
                  className="text-sm leading-relaxed"
                  style={{
                    color: '#3E3B34',
                    fontFamily: "'Noto Serif JP', serif",
                  }}
                >
                  {b}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        ref={ctaRef as React.RefObject<HTMLElement>}
        className="relative py-24 md:py-28 overflow-hidden"
        style={{ background: '#0A192F' }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute"
            style={{
              width: '500px',
              height: '500px',
              background: 'radial-gradient(circle, rgba(201,162,39,0.06) 0%, transparent 65%)',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%,-50%)',
              filter: 'blur(60px)',
            }}
          />
        </div>
        <div
          className="relative z-10 text-center px-6 max-w-2xl mx-auto"
          style={{
            opacity: ctaVisible ? 1 : 0,
            transform: ctaVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-12 h-px" style={{ background: 'linear-gradient(90deg, transparent, #C9A227)' }} />
            <i className="ri-sparkling-2-line text-lg" style={{ color: '#C9A227' }} />
            <div className="w-12 h-px" style={{ background: 'linear-gradient(90deg, #C9A227, transparent)' }} />
          </div>
          <h2
            className="text-2xl md:text-3xl leading-snug mb-5"
            style={{
              fontFamily: "'Noto Serif JP', 'Playfair Display', serif",
              fontWeight: 500,
              color: '#FFFFFF',
              letterSpacing: '0.04em',
            }}
          >
            {t('seo.servicePageCtaPrefix')}{config.pageLabel}
            <br />
            <span style={{ color: '#C9A227' }}>{t('seo.servicePageCtaHighlight')}</span>
          </h2>
          <p
            className="mb-10 text-sm leading-relaxed"
            style={{
              color: 'rgba(255,255,255,0.5)',
              fontFamily: "'Noto Serif JP', serif",
            }}
          >
            {t('seo.servicePageCtaDesc')}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 whitespace-nowrap cursor-pointer no-underline text-sm tracking-wider transition-all duration-300"
            style={{
              fontFamily: "'Noto Serif JP', serif",
              fontWeight: 500,
              padding: '14px 40px',
              background: '#C9A227',
              color: '#0A192F',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = '#D4AF37';
              (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = '#C9A227';
              (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
            }}
          >
            {t('nav.contact')}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      <ElegantFooter />
    </div>
  );
}
